import React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import ContactForm from './ContactForm';
import SEOHead from './SEOHead';

const headlines = [
  { line1: 'Run your business.', line2: 'Not your admin.' },
  { line1: 'More revenue.', line2: 'Not more hours.' },
  { line1: 'Win more work.', line2: 'Chase less of it.' },
  { line1: 'Every enquiry answered.', line2: 'Even the 9pm ones.' },
  { line1: 'Less time on admin.', line2: 'More time earning.' },
];

const Hero = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (prefersReducedMotion) return;

    const startCycle = () => {
      intervalRef.current = setInterval(() => {
        if (document.hidden) return;
        setIsAnimating(true);
        setTimeout(() => {
          setHeadlineIndex((prev) => (prev + 1) % headlines.length);
          setIsAnimating(false);
        }, 500);
      }, 3500);
    };

    startCycle();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (!intervalRef.current) {
        startCycle();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [prefersReducedMotion]);

  const handleGetStarted = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).voiceflow?.chat?.open) {
      try {
        (window as any).voiceflow.chat.open();
        return;
      } catch (error) {
        console.error('Error opening Voiceflow:', error);
      }
    }
    setIsContactFormOpen(true);
  }, []);

  const handleBookCall = useCallback(() => {
    setIsContactFormOpen(true);
  }, []);

  const handleCloseContactForm = useCallback(() => {
    setIsContactFormOpen(false);
  }, []);

  const getVariantStyle = (isActive: boolean): React.CSSProperties => {
    if (prefersReducedMotion) {
      return {
        opacity: isActive ? 1 : 0,
        visibility: isActive ? 'visible' : 'hidden',
      };
    }
    return {
      opacity: isActive ? (isAnimating ? 0 : 1) : 0,
      transform: isActive && isAnimating ? 'translateY(-12px)' : 'translateY(0)',
      transition: 'opacity 500ms ease-in-out, transform 500ms ease-in-out',
      visibility: isActive ? 'visible' : 'hidden',
    };
  };

  return (
    <>
      <SEOHead 
        title="DM.AI 4u — Websites, AI and Automation for Small Businesses"
        description="Websites, AI chat, automated outreach, bookings and custom software for small businesses. Built in West Yorkshire by someone who runs a business too."
      />
    <main className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-36 sm:pt-40 md:pt-44 lg:pt-48 pb-16 sm:pb-20">
      {/* Background layer — all visual effects clipped here, never touching the text */}
      <div className="absolute inset-0 overflow-hidden tech-grid md:bg-transparent mobile-purple-bg" aria-hidden="true">
        {/* Floating Particles */}
        <div className="floating-particles hidden md:block">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 6}s`,
                animationDuration: `${6 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-48 h-48 md:w-96 md:h-96 bg-purple-600/20 md:bg-purple-600/10 rounded-full blur-2xl md:blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 md:w-96 md:h-96 bg-blue-500/20 md:bg-blue-500/10 rounded-full blur-2xl md:blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-64 md:h-64 bg-emerald-500/15 md:bg-emerald-500/5 rounded-full blur-2xl md:blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />

        {/* Mobile-only vibrant orbs */}
        <div className="absolute top-10 right-10 w-32 h-32 mobile-orb-purple rounded-full blur-xl block md:hidden" />
        <div className="absolute bottom-10 left-10 w-40 h-40 mobile-orb-blue rounded-full blur-xl block md:hidden" />
        <div className="absolute top-1/3 left-10 w-24 h-24 mobile-orb-silver rounded-full blur-xl block md:hidden" />
        <div className="absolute bottom-1/3 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/30 to-blue-400/30 rounded-full blur-xl animate-pulse block md:hidden" style={{ animationDelay: '2.5s' }} />

        {/* Tech Decorative Elements */}
        <div className="absolute top-1/2 left-4 sm:left-10 hidden lg:block">
          <div className="w-px h-32 bg-gradient-to-b from-purple-600 via-blue-500 to-emerald-400 opacity-40 animate-pulse" />
          <div className="w-2 h-2 bg-blue-500 rounded-full -mt-1 animate-ping" />
        </div>
        <div className="absolute top-1/2 right-4 sm:right-10 hidden lg:block">
          <div className="w-px h-32 bg-gradient-to-b from-emerald-400 via-blue-500 to-purple-600 opacity-40 animate-pulse" />
          <div className="w-2 h-2 bg-emerald-400 rounded-full -mt-1 animate-ping" style={{ animationDelay: '1s' }} />
        </div>

        {/* Corner Tech Elements */}
        <div className="absolute top-20 sm:top-24 left-4 sm:left-20 w-8 h-8 sm:w-10 sm:h-10 border-l-2 border-t-2 border-blue-500/40 hidden md:block animate-pulse" />
        <div className="absolute top-20 sm:top-24 right-4 sm:right-20 w-8 h-8 sm:w-10 sm:h-10 border-r-2 border-t-2 border-emerald-400/40 hidden md:block animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 sm:bottom-24 left-4 sm:left-20 w-8 h-8 sm:w-10 sm:h-10 border-l-2 border-b-2 border-purple-600/40 hidden md:block animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-20 sm:bottom-24 right-4 sm:right-20 w-8 h-8 sm:w-10 sm:h-10 border-r-2 border-b-2 border-red-400/40 hidden md:block animate-pulse" style={{ animationDelay: '3s' }} />
      </div>

      {/* Content layer — no overflow clipping, text flows freely */}
      <div className="relative text-center max-w-5xl mx-auto">
        {/* Main Headline — CSS grid stack: all variants share one cell, container auto-sizes to tallest */}
        <div className="mb-6 sm:mb-8 headline-stack">
          {headlines.map((headline, index) => {
            const isActive = index === headlineIndex;
            return (
              <h1
                key={index}
                aria-hidden={!isActive}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display text-white tracking-wide leading-[1.08] px-2 font-black m-0 border-0"
                style={getVariantStyle(isActive)}
              >
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-300 to-silver-200 drop-shadow-2xl">
                  {headline.line1}
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-silver-300 to-blue-400 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-wider drop-shadow-2xl">
                  {headline.line2}
                </span>
              </h1>
            );
          })}
        </div>

        {/* Subheadline */}
        <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-8 sm:mb-10 font-tech tracking-wide animate-fade-in-up animation-delay-600 max-w-4xl mx-auto leading-relaxed px-4">
          The chasing, the quoting, the follow-ups, the enquiry that came in at 9pm and never got answered. DM.AI 4U builds the websites and the AI that take that half off you — so the work that comes in gets handled while you get on with the job. Run from West Yorkshire by someone who runs a business too.
        </p>

        {/* Key Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10 animate-fade-in-up animation-delay-800 max-w-4xl mx-auto px-4">
          <div className="p-4 sm:p-6 rounded-2xl bg-gray-900/30 md:bg-gray-900/30 bg-purple-800/20 border border-gray-800/50 md:border-gray-800/50 border-purple-400/30 backdrop-blur-sm mobile-card-glow">
            <div className="text-2xl sm:text-3xl font-bold text-purple-400 md:text-purple-400 mobile-accent-purple mb-2 font-robotic">24/7</div>
            <div className="text-sm sm:text-base text-gray-300 md:text-gray-300 mobile-accent-silver font-tech">AI Support</div>
          </div>
          <div className="p-4 sm:p-6 rounded-2xl bg-gray-900/30 md:bg-gray-900/30 bg-blue-800/20 border border-gray-800/50 md:border-gray-800/50 border-blue-400/30 backdrop-blur-sm mobile-card-glow">
            <div className="text-2xl sm:text-3xl font-bold text-blue-400 md:text-blue-400 mobile-accent-blue mb-2 font-robotic">Built, Not Bought</div>
            <div className="text-sm sm:text-base text-gray-300 md:text-gray-300 mobile-accent-silver font-tech">Everything made to fit your business</div>
          </div>
          <div className="p-4 sm:p-6 rounded-2xl bg-gray-900/30 md:bg-gray-900/30 bg-emerald-800/20 border border-gray-800/50 md:border-gray-800/50 border-emerald-400/30 backdrop-blur-sm mobile-card-glow">
            <div className="text-2xl sm:text-3xl font-bold text-emerald-400 md:text-emerald-400 mobile-accent-emerald mb-2 font-robotic">No Contracts</div>
            <div className="text-sm sm:text-base text-gray-300 md:text-gray-300 mobile-accent-silver font-tech">Cancel anytime</div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="animate-fade-in-up animation-delay-1000 flex flex-col sm:flex-row gap-4 justify-center items-center px-4 mb-8 sm:mb-10">
          <button 
            onClick={handleBookCall}
            type="button"
            aria-label="Book a demo call with DM.AI 4u"
            className="group relative w-full sm:w-auto px-10 sm:px-12 py-5 sm:py-6 border border-gray-600/50 rounded-2xl text-white font-medium text-lg sm:text-xl tracking-wide hover:border-purple-400/50 transition-all duration-300 hover:bg-gray-800/50 backdrop-blur-sm overflow-hidden cursor-pointer"
          >
            <span className="relative z-10 font-robotic">Book Demo Call</span>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-purple-500/10 to-gray-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactForm 
        isOpen={isContactFormOpen} 
        onClose={handleCloseContactForm} 
      />
    </main>
    </>
  );
};

export default Hero;
